import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller.ts"
import { WorkPeriodTemplate } from "@/models/work-period-template.model.ts"
import { WorkPeriod } from "@/models/work-period.model.ts"
import type { IWorkPeriodTemplate } from "@/interfaces/api/work-period-template.interface.ts"
import { WorkPeriodTemplateRepository } from "@/repositories/work-period-template-repository.ts"
import type { IStoreFunctionsConfig } from "@/store/base-functions/base-store-functions.ts"
import { useWorkPeriodTemplateStore } from "@/store/work-period-templates.store.ts"
import { storeToRefs } from "pinia"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface.ts"
import { ModelFactory } from "@/factories/model-factory.ts"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import type { Moment } from "moment"

export class WorkPeriodTemplateController extends GenericModelController<
  WorkPeriodTemplate,
  IWorkPeriodTemplate,
  WorkPeriodTemplateRepository
> {
  constructor(storeFunctionsConfig?: IStoreFunctionsConfig<WorkPeriodTemplate>) {
    if (!storeFunctionsConfig) {
      const store = useWorkPeriodTemplateStore()
      const { workPeriodTemplates } = storeToRefs(store)
      storeFunctionsConfig = {
        managed: false,
        collection: workPeriodTemplates
      }
    }

    super(WorkPeriodTemplate, storeFunctionsConfig, new WorkPeriodTemplateRepository())
  }

  public getListForKnownBusinessEntityContact(kbec: KnownBusinessEntityContact) {
    const query = new ODataQueryBuilder().eq("owner_id", kbec.id).deep()
    return this.getList({ query })
  }

  /**
   * Atomically creates a work period (with its activities) from a template for the given date.
   * Delegates the projection and persistence to the server, returning an unmanaged WorkPeriod
   * model built from the response, or undefined when creation failed (e.g. validation error).
   * @param templateId The id of the template to instantiate
   * @param date The date to project the template's times onto
   * @param config Optional request configuration (e.g. validation status-code handlers)
   * @param draft Optional corrected application values; does not update the saved template
   */
  public async createWorkPeriod(
    templateId: string,
    date: Moment,
    config?: IRepositoryRequestConfig,
    draft?: WorkPeriod
  ): Promise<WorkPeriod | undefined> {
    const response = await this.apiFunctions
      .getRepository()
      .createWorkPeriod(templateId, date.format("YYYY-MM-DD"), config, draft)

    if (response?.status !== 201 || !response.data?.id) {
      return undefined
    }

    return ModelFactory.createModel(WorkPeriod, response.data, false)
  }
}
